import {
  Text,
  Input as ChakraInput,
  InputProps as ChakraInputProps,
  Box,
} from "@chakra-ui/react";
import {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  useEffect,
  useState,
} from "react";
import { useFormContext } from "react-hook-form";
import CircleHelpIcon from "../icons/CircleHelpIcon";
import Tooltip from "../Tooltip";

const Input = forwardRef<
  HTMLInputElement,
  ChakraInputProps & {
    name: string;
    unit?: string;
    error?: boolean;
    tooltip?: string;
    setValue?: (value?: string) => void;
  }
>(
  (
    {
      children,
      placeholder,
      onChange,
      onKeyDown,
      onBlur,
      onFocus,
      unit,
      error = false,
      tooltip,
      value: providedValue,
      setValue: providedSetValue,
      ...props
    },
    ref
  ) => {
    const [innerValue, setInnerValue] = useState("");

    const value = providedValue ?? innerValue
    const setValue = providedSetValue ?? setInnerValue

    const [active, setActive] = useState(
      ["date"].includes(props.type ?? "text") || providedValue !== undefined ? true : false
    );
    const context = useFormContext();
    const watcher = context ? context.watch(props.name) : null;
    useEffect(() => {
      if (watcher != null && watcher != "" && watcher !== value) {
        setActive(watcher !== "");
      }
    }, [watcher, value, setActive]);

    useEffect(() => {
      if (props.defaultValue !== undefined && props.defaultValue !== null) {
        const string = props.defaultValue.toString();
        setValue(string);
        if (context) {
          context.setValue(props.name, string);
        }
        setActive(string !== "");
      }
    }, [props.defaultValue]);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      setValue(event.target.value);
      if (context) {
        context.setValue(props.name, event.target.value);
      }
      setActive(event.target.value !== "");
      if (onChange) {
        onChange(event);
      }
    };

    const handleBlur = (event: FocusEvent<HTMLInputElement, Element>) => {
      if (value === "") {
        setActive(false);
      } else {
        setActive(true);
      }
      if (onBlur) {
        onBlur(event);
      }
    };

    const handleFocus = (event: FocusEvent<HTMLInputElement, Element>) => {
      setActive(true);
      if (onFocus) {
        onFocus(event);
      }
    };

    return (
      <Box position="relative" w="100%">
        <ChakraInput
          ref={ref}
          id={props.id}
          type={props.type}
          step={props.step}
          min={props.min}
          max={props.max}
          name={props.name}
          isDisabled={props.isDisabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          value={value}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          border={error ? "solid 2px" : undefined}
          borderColor={error ? "rgb(229, 62, 62)" : undefined}
          bgColor="white"
          w="100%"
        >
          {children}
        </ChakraInput>
        <Text
          position="absolute"
          top="0%"
          left="5px"
          transform={
            active
              ? "translate(10px,-10px) scale(0.8)"
              : "translate(10px,8px) scale(1)"
          }
          color="gray.500"
          px={1}
          transformOrigin="top left"
          transition="all .2s ease-out"
          pointerEvents="none"
          bg="white"
          w="fit-content"
          h="fit-content"
          zIndex={5}
        >
          {placeholder}
        </Text>
        {unit && (
          <Text
            position="absolute"
            top="0%"
            right="20px"
            transform={
              active
                ? "translate(10px,-10px) scale(0.8)"
                : "translate(10px,8px) scale(1)"
            }
            color="gray.500"
            px={1}
            transformOrigin="top left"
            transition="all .2s ease-out"
            pointerEvents="none"
            bg="white"
            w="fit-content"
            h="fit-content"
            zIndex={5}
          >
            {unit}
          </Text>
        )}
        {tooltip && (
          <Text position="absolute" right="1rem" top="0.48rem">
            <Tooltip content={tooltip}>
              <CircleHelpIcon w='18px' h='18px' color='gray.400'></CircleHelpIcon>
            </Tooltip>
          </Text>
        )}
      </Box>
    );
  }
);

export default Input;
